import LineLogo from 'icon/logo_line.svg';
import FacebookLogo from 'icon/logo_facebook.svg';
import TwitterLogo from 'icon/logo_twitter.svg';
import WhatsAppLogo from 'icon/logo_whatsapp.svg';
import LinkLogo from 'icon/link.svg';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { ClickPosition } from '~/utils/click-position';

interface UseShareConfigProps {
  title?: string;
  href?: string;
  handleClose: () => void;
}

interface ShareConfig {
  text: string;
  Icon: FC;
  color: string;
  className?: string;
  onClick: () => void;
}

/**
 * 分享的配置
 */
export const useShareConfig = (props: UseShareConfigProps): ShareConfig[] => {
  const { handleClose } = props;
  const { t } = useTranslation();
  const { region } = useAtomValue($Locale);

  const _title = (): string => {
    return props.title || document.title;
  };

  const _href = (): string => {
    let href: string;
    if (props.href) {
      href = props.href;
    } else {
      href = (document.querySelector("link[rel='canonical']") as HTMLLinkElement).href;
    }

    try {
      // SEO 页面分享出去的页面，带上 SEO 的 pid
      const url = new URL(href);
      url.searchParams.set('pid', 'website_seo_share');
      return url.toString();
    } catch (e) {
      return `https://www.lemon8-app.com?region=${region}&pid=website_seo_share`;
    }
  };

  const handleShareLine = (): void => {
    window.Tea('wap_click', {
      current_position: ClickPosition.share_panel_icon,
      page_click_id: 'line',
    });
    window.open(`https://line.me/R/share?text=${encodeURIComponent(`${_title()} ${_href()}`)}`);
  };

  const handleShareFB = (): void => {
    window.Tea('wap_click', {
      current_position: ClickPosition.share_panel_icon,
      page_click_id: 'facebook',
    });
    try {
      FB.init({
        appId: '426786581540674',
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v14.0',
      });
      window.FB.ui({ method: 'share', mobile_iframe: true, href: _href() });
    } catch (e) {
      console.log('>>>>', e);
    }
  };

  const handleShareWhatsApp = (): void => {
    window.Tea('wap_click', {
      current_position: ClickPosition.share_panel_icon,
      page_click_id: 'whatsapp',
    });
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${_href()}`)}`);
  };

  const handleShareTwitter = (): void => {
    window.Tea('wap_click', {
      current_position: ClickPosition.share_panel_icon,
      page_click_id: 'twitter',
    });

    const text = encodeURIComponent(_title());
    const url = encodeURIComponent(_href());
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`);
  };

  const handleCopyLink = async (): Promise<void> => {
    window.Tea('wap_click', {
      current_position: ClickPosition.share_panel_icon,
      page_click_id: 'copy_link',
    });
    try {
      await navigator.clipboard.writeText(_href());
    } catch (e) {
      console.log(e);
    }
    handleClose();
  };

  return [
    {
      text: 'Line',
      Icon: LineLogo,
      color: '#00BD64',
      onClick: handleShareLine,
    },
    {
      text: 'Facebook',
      Icon: FacebookLogo,
      color: '#3B77EA',
      onClick: handleShareFB,
    },
    {
      text: 'WhatsApp',
      Icon: WhatsAppLogo,
      color: '#47C757',
      onClick: handleShareWhatsApp,
    },
    {
      text: 'Twitter',
      Icon: TwitterLogo,
      color: '#1DA1F2',
      onClick: handleShareTwitter,
    },
    {
      text: t('copy_link'),
      Icon: LinkLogo,
      color: '#FFF',
      className: 'copy-link',
      onClick: handleCopyLink,
    },
  ];
};
